import {
  nutritionTargetSchema,
  userRecipeSettingSchema,
  type NutritionTarget,
  type UserRecipeSetting,
} from '@cadencia/contracts';
import { logger } from '../../lib/logger.js';
import { addDays } from '../../lib/dates.js';
import { db } from '../db.js';
import { newSyncColumns, putRow, type WriteContext } from '../writes.js';
import { parchear } from '../parchear.js';

/**
 * Objetivos diarios y preferencias por receta. El peso vive en `progressRepo`.
 *
 * El menu de la semana vive en `mealPlanRepo`; aqui esta lo que el motor
 * necesita ANTES de generarlo: cuanto gasta el usuario y que recetas le
 * gustan.
 *
 * Los `parse` de este fichero estan todos en caminos de ESCRITURA. Al leer se
 * usa `safeParse` y se devuelve la fila tal cual, como manda ADR-035: una fila
 * rara no puede dejar la pantalla de nutricion en blanco.
 */

function normalizar<T>(
  filas: readonly unknown[],
  esquema: { safeParse: (value: unknown) => { success: boolean; data?: unknown } },
  tabla: string,
): T[] {
  const salida: T[] = [];
  let invalidas = 0;
  for (const fila of filas) {
    const resultado = esquema.safeParse(fila);
    salida.push((resultado.success ? resultado.data : fila) as T);
    if (!resultado.success) invalidas += 1;
  }
  if (invalidas > 0) {
    logger.warn('db', 'NUTRITION_SHAPE_MISMATCH', 'Filas locales que no encajan con su contrato', {
      tabla,
      invalidas,
    });
  }
  return salida;
}

// --------------------------------------------------------------------------
// Objetivos diarios
// --------------------------------------------------------------------------

export async function getTargetForDate(
  userId: string,
  date: string,
): Promise<NutritionTarget | undefined> {
  const filas = await db.nutritionTargets.where('targetDate').equals(date).toArray();
  const vivas = filas.filter((fila) => fila.userId === userId && fila.deletedAt === null);
  return normalizar<NutritionTarget>(vivas, nutritionTargetSchema, 'nutritionTargets')[0];
}

export async function listTargetsBetween(
  userId: string,
  from: string,
  toExclusive: string,
): Promise<NutritionTarget[]> {
  const filas = await db.nutritionTargets.where('userId').equals(userId).toArray();
  const vivas = filas.filter(
    (fila) =>
      fila.deletedAt === null && fila.targetDate >= from && fila.targetDate < toExclusive,
  );
  return normalizar<NutritionTarget>(vivas, nutritionTargetSchema, 'nutritionTargets').sort((a, b) =>
    a.targetDate.localeCompare(b.targetDate),
  );
}

export type NutritionTargetInput = Omit<
  NutritionTarget,
  keyof ReturnType<typeof newSyncColumns>
>;

/**
 * Escribe el objetivo de un dia.
 *
 * Una fila por dia: si ya existe, se reescribe conservando su identificador.
 * El objetivo «queda congelado» (§12 de DATA_MODEL.md) en el sentido de que no
 * se recalcula solo cada vez que se abre la pantalla, no en el de que sea
 * inmutable: cambiar de fase o recalibrar el TDEE si lo cambian, y entonces
 * tiene que ser la MISMA fila o el dia tendria dos objetivos.
 */
export async function saveTarget(
  ctx: WriteContext,
  input: NutritionTargetInput,
): Promise<NutritionTarget> {
  const previo = await getTargetForDate(ctx.userId, input.targetDate);
  const row =
    previo === undefined
      ? nutritionTargetSchema.parse({ ...newSyncColumns(ctx), ...input })
      : parchear(nutritionTargetSchema, previo, input, {
          tabla: 'nutrition_targets',
          id: previo.id,
        });
  return putRow(ctx, 'nutritionTargets', row);
}

// El peso diario vive en `progressRepo` desde el 21/09/2026: es una senal
// corporal del atleta, no un dato nutricional. La nutricion lo lee de alli.

// --------------------------------------------------------------------------
// Preferencias por receta
// --------------------------------------------------------------------------

export async function listRecipeSettings(userId: string): Promise<UserRecipeSetting[]> {
  const filas = await db.userRecipeSettings.where('userId').equals(userId).toArray();
  const vivas = filas.filter((fila) => fila.deletedAt === null);
  return normalizar<UserRecipeSetting>(vivas, userRecipeSettingSchema, 'userRecipeSettings');
}

export async function favoriteSlugs(userId: string): Promise<string[]> {
  return (await listRecipeSettings(userId))
    .filter((ajuste) => ajuste.isFavorite)
    .map((ajuste) => ajuste.recipeSlug);
}

async function upsertRecipeSetting(
  ctx: WriteContext,
  recipeSlug: string,
  parche: Partial<Omit<UserRecipeSetting, 'recipeSlug'>>,
): Promise<UserRecipeSetting> {
  const ajustes = await listRecipeSettings(ctx.userId);
  const previo = ajustes.find((ajuste) => ajuste.recipeSlug === recipeSlug);
  const row = userRecipeSettingSchema.parse(
    previo === undefined
      ? {
          ...newSyncColumns(ctx),
          recipeSlug,
          isFavorite: false,
          rating: null,
          lastUsedOn: null,
          ...parche,
        }
      : { ...previo, ...parche },
  );
  return putRow(ctx, 'userRecipeSettings', row);
}

export function setFavorite(
  ctx: WriteContext,
  recipeSlug: string,
  isFavorite: boolean,
): Promise<UserRecipeSetting> {
  return upsertRecipeSetting(ctx, recipeSlug, { isFavorite });
}

export function rateRecipe(
  ctx: WriteContext,
  recipeSlug: string,
  rating: number | null,
): Promise<UserRecipeSetting> {
  return upsertRecipeSetting(ctx, recipeSlug, { rating });
}

/**
 * Marca cuando se planifico cada receta.
 *
 * Alimenta la ventana de variedad del §5.4 SIN depender de que el menu de esa
 * semana siga existiendo: si el usuario regenera la semana, el historial de
 * recetas usadas tiene que sobrevivir o la variedad se reinicia sola.
 */
export async function markRecipesUsed(
  ctx: WriteContext,
  usos: readonly { recipeSlug: string; usedOn: string }[],
): Promise<void> {
  const ultimo = new Map<string, string>();
  for (const uso of usos) {
    const previo = ultimo.get(uso.recipeSlug);
    if (previo === undefined || uso.usedOn > previo) ultimo.set(uso.recipeSlug, uso.usedOn);
  }
  for (const [recipeSlug, usedOn] of ultimo) {
    const ajustes = await listRecipeSettings(ctx.userId);
    const previo = ajustes.find((ajuste) => ajuste.recipeSlug === recipeSlug);
    if (previo !== undefined && previo.lastUsedOn !== null && previo.lastUsedOn >= usedOn) continue;
    await upsertRecipeSetting(ctx, recipeSlug, { lastUsedOn: usedOn });
  }
}

/** Historial de uso para `MenuContext.history`, dentro de la ventana pedida. */
/**
 * Recetas usadas en la ventana de variedad ANTES de `desdeAntesDe`.
 *
 * EL EXTREMO SUPERIOR NO ES UN DETALLE (08/09/2026). Sin el, regenerar una
 * semana se dejaba sin recetas a si mismo: la primera generacion marca cada
 * receta como usada en un dia DE ESA SEMANA, y al regenerar, esas fechas caian
 * dentro de la ventana y `esApta` las bloqueaba diez dias. El resultado era un
 * menu mucho peor —medido con los E2E: cuatro de seis dias en `partial`— sin
 * ningun error por ninguna parte.
 *
 * Y no era nuevo: le pasaba desde la Fase 4 al boton «Rehacer el menu de esta
 * semana». Se vio ahora porque cambiar el esquema de comidas regenera, asi que
 * dejo de ser un caso que casi nadie ejecuta.
 *
 * Los usos de la propia semana que SI se conservan —los dias anteriores al
 * corte, cuando se regenera solo una parte— los añade `generateWeekPlan` a
 * mano, porque esos platos no se descartan y siguen contando para la variedad.
 */
export async function recipeHistory(
  userId: string,
  desdeAntesDe: string,
  ventanaDias: number,
): Promise<{ recipeSlug: string; usedOn: string }[]> {
  const limite = addDays(desdeAntesDe, -ventanaDias);
  return (await listRecipeSettings(userId))
    .filter(
      (ajuste) =>
        ajuste.lastUsedOn !== null &&
        ajuste.lastUsedOn >= limite &&
        ajuste.lastUsedOn < desdeAntesDe,
    )
    .map((ajuste) => ({ recipeSlug: ajuste.recipeSlug, usedOn: ajuste.lastUsedOn as string }));
}
